import "../../css/Authentication/login.css";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { loginApi, loginValidationApi, getCustomerListApi } from "../../api/authApi";
import { useAuth } from "../../context/AuthContext";
import { getDeviceId } from "../../utils/device";
import { Tooltip } from "primereact/tooltip";

export default function Login() {
    const navigate = useNavigate();
    const { setUser, setCustomerList } = useAuth();

    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ userName: "", password: "" });
    const [apiError, setApiError] = useState("");

    const validateUserName = () => {
        let isValid = true;
        if (!userName.trim()) {
            setErrors(prev => ({ ...prev, userName: "Please enter your username" }));
            isValid = false;
        } else {
            setErrors(prev => ({ ...prev, userName: "" }));
        }
        return isValid;
    };

    const validatePassword = () => {
        let isValid = true;
        if (!password.trim()) {
            setErrors(prev => ({ ...prev, password: "Please enter your password" }));
            isValid = false;
        } else {
            setErrors(prev => ({ ...prev, password: "" }));
        }
        return isValid;
    };

    const validateForm = () => {
        const isUserValid = validateUserName();
        const isPassValid = validatePassword();
        return isUserValid && isPassValid;
    };

    const handleLogin = async () => {
        if (!validateForm()) return;
        try {
            setLoading(true);
            setApiError("");

            const payload = { userName, password, DeviceID: getDeviceId() };

            const [validationRes, loginRes] = await Promise.all([
                loginValidationApi(payload),
                loginApi(payload)
            ]);

            const loginMsg = loginRes?.Message?.[0];
            const authUser = loginRes?.UserData?.[0];

            if (loginMsg?.ReturnMessageType === "SUCCESS" && authUser) {
                const customerRes = await getCustomerListApi({
                    Customer_ID: authUser.Customer_ID,
                    IsAdmin: authUser.IsAdmin,
                    SearchText: ""
                });

                const customerList = customerRes?.Data || [];

                localStorage.setItem("authUser", JSON.stringify({ ...authUser, isAuthenticated: true }));
                localStorage.setItem("authToken", loginRes?.JwtToken);
                localStorage.setItem("customerList", JSON.stringify(customerList));

                if (rememberMe) {
                    localStorage.setItem("rememberedUserName", userName);
                    localStorage.setItem("rememberedPassword", password);
                } else {
                    localStorage.removeItem("rememberedUserName");
                    localStorage.removeItem("rememberedPassword");
                }
                
                setUser({ ...authUser, isAuthenticated: true });
                setCustomerList(customerList);

                navigate("/dashboard");
            } else {
                setApiError(loginMsg?.ReturnMessageDesc || "Login failed");
            }
        } catch (error) {
            setApiError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleLogin();
        }
    };

    return (
        <div className="login-root">
            <div className="login-card card-vertical">
                <div className="card-image">
                    <img src="/assets/images/logo/Midamas_Logo.png" alt="Login Banner" className="card-banner-img" />
                    <div className="card-image-overlay"></div>
                </div>
                <div className="card-form">
                    <h3>Welcome Back!</h3>
                    <p className="card-subtitle">Sign in to continue</p>

                    <div className="field login-field">
                        <InputText
                            placeholder="Username or Email"
                            className={`w-full ${errors.userName ? "p-invalid" : ""}`}
                            style={{ fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}
                            value={userName}
                            onChange={(e) => { setUserName(e.target.value); setErrors({ ...errors, userName: "" }); }}
                            onBlur={validateUserName}
                            onKeyDown={handleKeyDown}
                            id="login_UserName"
                        />
                        {errors.userName && (
                            <small className="error-text">{errors.userName}</small>
                        )}
                    </div>

                    <div className="field login-field">
                        <Password
                            placeholder="Password"
                            feedback={false}
                            toggleMask
                            className={`w-full ${errors.password ? "p-invalid" : ""}`}
                            inputStyle={{ fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", width: '100%' }}
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setErrors({ ...errors, password: "" }); }}
                            onBlur={validatePassword}
                            onKeyDown={handleKeyDown}
                            id="login_Password"
                        />
                        {errors.password && (
                            <small className="error-text">{errors.password}</small>
                        )}
                    </div>

                    <div className="remember-me">
                        <Checkbox inputId="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.checked)} />
                        <label htmlFor="rememberMe">Remember me</label>
                    </div>
                    <Button label={loading ? "LOGGING IN..." : "LOGIN"} className="login-btn w-full mt-3" loading={loading} disabled={loading} onClick={handleLogin} />
                    {apiError && <div className="api-error mt-2">{apiError}</div>}

                    <div className="login-links">
                        <span className="disabled-link" data-pr-tooltip="Available soon" data-pr-position="bottom">
                            Forgot your password?
                        </span>

                        <Tooltip target=".disabled-link" />
                    </div>
                </div>
            </div>
            <div className="login-shape-left"></div>
            <div className="login-shape-right"></div>
        </div>
    );
}